// Tab chỉ dành cho Đại lý / CTV:
//  · "Công nợ cấp dưới" — để giúp nhắc; ShopTrack là bên thu nợ (§4.5). Chênh lệch / hoa hồng
//    của bạn trên đơn tạo nợ đang được tạm giữ tới khi khoản nợ được thu.
//  · "Hoa hồng CTV đã trừ" (Đại lý) — theo từng CTV, từng tháng (§4.6).

import { useQuery } from '@tanstack/react-query'
import { Phone } from 'lucide-react'
import { get } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import { money, phoneDisplay } from '@/lib/format'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { LoadError } from '@/components/ui/data-notice'
import DataTable, { type DataTableColumn } from '@/components/ui/data-table'
import { useNppName } from '@/components/layout/shell-model'
import { MONTH_STATUS_LABEL, monthLabel } from '@/components/commissions/types'
import { FOLLOW_UP_LABEL, UNIT_TYPE_LABEL, type CommissionDeductionRow, type NetworkDebtRow } from './types'

export function NetworkDebts() {
  const selfId = getAuthState().activeShopId
  const nppName = useNppName()
  const q = useQuery({ queryKey: ['reconcile', 'network-debts'], queryFn: () => get<{ debts: NetworkDebtRow[] }>('/reconcile/network-debts') })
  const rows = q.data?.debts ?? []
  const owed = (r: NetworkDebtRow) => r.debt + (r.shipDebt ?? 0)
  const total = rows.reduce((s, r) => s + owed(r), 0)

  const columns: DataTableColumn<NetworkDebtRow>[] = [
    {
      key: 'unit',
      header: 'Đơn vị',
      priority: 'primary',
      cell: (r) => (
        <span className="block min-w-0">
          <span className="block truncate font-medium text-ink">{r.name}</span>
          <span className="block text-xs text-muted">
            {UNIT_TYPE_LABEL[r.payeeType] ?? r.payeeType}
            {r.parent && r.parent.id !== selfId ? ` · dưới ${r.parent.name}` : ' · trực thuộc bạn'}
          </span>
        </span>
      ),
    },
    {
      key: 'debt',
      header: 'Đang nợ',
      align: 'right',
      cell: (r) => (
        <span className="block text-right">
          <span className={cn('whitespace-nowrap font-mono font-semibold tabular-nums', owed(r) > 0 ? 'text-danger' : 'text-muted')}>{money(owed(r))}</span>
          {(r.shipDebt ?? 0) > 0 && <span className="block whitespace-nowrap text-2xs text-muted">gồm ship cho nợ {money(r.shipDebt!)}</span>}
        </span>
      ),
    },
    {
      key: 'upcoming',
      header: 'Sắp phát sinh',
      align: 'right',
      cell: (r) => <span className="whitespace-nowrap font-mono tabular-nums text-inkSoft">{r.upcoming > 0 ? money(r.upcoming) : '—'}</span>,
    },
    {
      key: 'age',
      header: 'Nợ từ',
      cell: (r) => <span className="whitespace-nowrap tabular-nums text-inkSoft">{r.ageDays == null ? '—' : r.ageDays === 0 ? 'Hôm nay' : `${r.ageDays} ngày`}</span>,
    },
    {
      key: 'follow',
      header: `${nppName} đòi`,
      cell: (r) => <span className="text-inkSoft">{FOLLOW_UP_LABEL[r.followUp?.status ?? 'none'] ?? r.followUp?.status}</span>,
    },
    {
      key: 'call',
      header: '',
      priority: 'action',
      cell: (r) =>
        r.phone ? (
          <Button asChild size="sm" variant="outline" className="gap-1.5">
            <a href={`tel:${r.phone}`} aria-label={`Gọi ${r.name}`}>
              <Phone className="size-4" /> {phoneDisplay(r.phone)}
            </a>
          </Button>
        ) : null,
    },
  ]

  return (
    <section className="space-y-3">
      {total > 0 && <p className="max-w-[75ch] text-sm text-inkSoft">Tổng đang nợ: {money(total)}.</p>}
      <div className="overflow-hidden rounded-xl bg-raise shadow-card">
        {q.isError ? (
          <LoadError onRetry={() => q.refetch()}>Không tải được công nợ cấp dưới.</LoadError>
        ) : (
          <DataTable columns={columns} data={rows} rowKey={(r) => r.payeeKey} loading={q.isLoading} emptyLabel="Không có cấp dưới nào đang nợ." />
        )}
      </div>
    </section>
  )
}

export function CommissionDeductions() {
  const q = useQuery({ queryKey: ['reconcile', 'commission-deductions'], queryFn: () => get<{ rows: CommissionDeductionRow[] }>('/reconcile/commission-deductions') })
  const rows = q.data?.rows ?? []
  const totalDeducted = rows.reduce((s, r) => s + r.deducted, 0)

  const columns: DataTableColumn<CommissionDeductionRow>[] = [
    {
      key: 'month',
      header: 'Tháng',
      priority: 'secondary',
      cell: (r) => <span className="whitespace-nowrap tabular-nums text-ink">Tháng {monthLabel(r.month)}</span>,
    },
    { key: 'ctv', header: 'CTV', priority: 'primary', cell: (r) => <span className="font-medium text-ink">{r.ctvName}</span> },
    { key: 'orders', header: 'Số đơn tính chiết khấu', align: 'right', cell: (r) => <span className="tabular-nums text-ink">{r.totalOrders.toLocaleString('vi-VN')}</span> },
    { key: 'deducted', header: 'Đã trừ', align: 'right', cell: (r) => <span className="whitespace-nowrap font-mono font-semibold tabular-nums text-ink">{money(r.deducted)}</span> },
    { key: 'paid', header: 'Đã chi', align: 'right', cell: (r) => <span className="whitespace-nowrap font-mono tabular-nums text-inkSoft">{money(r.paid)}</span> },
    {
      key: 'status',
      header: 'Trạng thái',
      cell: (r) => (
        <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', r.monthStatus === 'closed' ? 'bg-ok/12 text-ok' : 'bg-info/12 text-info')}>
          {MONTH_STATUS_LABEL[r.monthStatus] ?? r.monthStatus}
        </span>
      ),
    },
  ]

  return (
    <section className="space-y-3">
      {totalDeducted > 0 && <p className="max-w-[75ch] text-sm text-inkSoft">Tổng đã trừ: {money(totalDeducted)}.</p>}
      <div className="overflow-hidden rounded-xl bg-raise shadow-card">
        {q.isError ? (
          <LoadError onRetry={() => q.refetch()}>Không tải được chiết khấu CTV đã trừ.</LoadError>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            rowKey={(r) => `${r.month}:${r.ctvShopId}`}
            loading={q.isLoading}
            emptyLabel="Chưa có chiết khấu CTV nào được trừ."
          />
        )}
      </div>
    </section>
  )
}
